/**
 * Service which manages shared generic data features in the store
 * @module services/data
 */
import isFunction from 'lodash/isFunction'
import isUndefined from 'lodash/isUndefined'
import {
  Map, List, Record, OrderedMap,
} from 'immutable'

/**
 * The key in the store key where records exist
 */
export const DATA_RECORDS_KEY = 'records'

/**
 * The key in the store key where the view exist
 */
export const DATA_VIEWS_KEY = 'views'

/**
 * The key in the store key where the application state exists
 */
export const DATA_APPLICATION_KEY = 'application'

/**
 * The key in the store key in the view where the records keys exist
 */
export const DATA_VIEWS_RECORD_KEYS_KEY = 'recordKeys'

/**
 * Data store view record kept on a list under the views propery
 * @property {Map} options The options used to generate the view also used as a key for the view
 * @property {Number} total The number of items in the view
 * @property {Boolean} processing True when the API client is fetching data
 * @property {Error|undefined} error An error returned when fetching data
 * @property {List<String[]>} recordKeys A list of keys that match the view options
 */
export const DataStoreViewRecord = Record({
  options: Map(),
  total: 0,
  processing: false,
  error: undefined,
  [DATA_VIEWS_RECORD_KEYS_KEY]: List(),
})

/**
 * Data record for a redux store which is the root shape for all store keys
 * @property {List<DataStoreViewRecord[]>} view A list of views
 * @property {List} views Views that defined the records that make up a view
 * @property {Map} records A dictionary of records key by primary key
 * @property {Map} application Any application state also the records draft
 * state when editing an existing record
 */
export const DataStoreRecord = Record({
  [DATA_VIEWS_KEY]: List(),
  [DATA_RECORDS_KEY]: Map(),
  [DATA_APPLICATION_KEY]: Map(),
})

/**
 * Find a view in the redux state data for the store by options and get the processing value
 * @param {Object} param0 The parameters
 * @param {Object} param0.data The data state from redux
 * @param {String} param0.storeKey The store key
 * @param {Map} param0.options The options passed to the view
 * @returns {Boolean|undefined} The processing value or undefined if not found
 */
export function getViewProcessing ({ data, storeKey, options = Map() } = {}) {
  const view = findView({ data, storeKey, options })
  if (!view) {
    return undefined
  }
  return view.get('processing')
}

/**
 * Find a view in the redux state data for the store by options and get the total value
 * @param {Object} param0 The parameters
 * @param {Object} param0.data The data state from redux
 * @param {String} param0.storeKey The store key
 * @param {Map} param0.options The options passed to the view
 * @returns {Boolean|undefined} The processing value or undefined if not found
 */
export function getViewTotal ({ data, storeKey, options = Map() } = {}) {
  const view = findView({ data, storeKey, options })
  if (!view) {
    return undefined
  }
  return view.get('total')
}

/**
 * Find a view in the redux state data for the store by options and get the error value
 * @param {Object} param0 The parameters
 * @param {Object} param0.data The data state from redux
 * @param {String} param0.storeKey The store key
 * @param {Map} param0.options The options passed to the view
 * @returns {Error|undefined} The processing value or undefined if not found
 */
export function getViewError ({ data, storeKey, options = Map() } = {}) {
  const view = findView({ data, storeKey, options })
  if (!view) {
    return undefined
  }
  return view.get('error')
}

/**
 * Find a view in the redux state data for the store by options
 * @param {Object} param0 The parameters
 * @param {Object} param0.data The data state from redux
 * @param {String} param0.storeKey The store key
 * @param {Map} param0.options The options passed to the view
 */
export function findView ({ data, storeKey, options = Map() } = {}) {
  if (!Map.isMap(options)) {
    throw new Error('The options param is expected to be a Map')
  }
  return getViews({ data, storeKey }).find((v) => {
    const viewOptions = v.get('options') || Map()
    return options.equals(viewOptions)
  })
}

/**
 * Filter views in the redux state data for the store by the filter function
 * @param {Object} param0 The parameters
 * @param {Object} param0.data The data state from redux
 * @param {String} param0.storeKey The store key
 * @param {Function} predicate The function to check all views, the view
 * will be returned when the function returns true
 * @returns {List} A list of view matching the filter
 */
export function filterViews ({ data, storeKey } = {}, predicate) {
  if (!isFunction(predicate)) {
    throw new Error('The predicate param is expected to be a function')
  }
  const dataStore = getDataStore({ data, storeKey })
  if (!dataStore) {
    return List()
  }
  return getViews({ data, storeKey }).filter(predicate)
}

/**
 * Find a view index in the redux state data for the store by options
 * @param {Object} param0 The parameters
 * @param {Object} param0.data The data state from redux
 * @param {String} param0.storeKey The store key
 * @param {Map} param0.options The options passed to the view
 */
export function findViewIndex ({ data, storeKey, options = Map() } = {}) {
  if (!Map.isMap(options)) {
    throw new Error('The options param is expected to be a Map')
  }
  return getViews({ data, storeKey }).findIndex((v) => {
    const viewOptions = v.get('options') || Map()
    return options.equals(viewOptions)
  })
}

/**
 * Get views in the redux state data for the store for the store key
 * @param {Object} param0 The parameters
 * @param {Object} param0.data The data state from redux
 * @param {String} param0.storeKey The store key
 */
export function getViews ({ data, storeKey } = {}) {
  const dataStore = getDataStore({ data, storeKey })
  if (!dataStore) {
    return List()
  }
  return dataStore.get(DATA_VIEWS_KEY)
}

/**
 * Get all of the records in the data store for a store key
 * @param {Object} param0 The parameters
 * @param {Object} param0.data The data state from redux
 * @param {String} param0.storeKey The store key
 * @returns {Map|undefined} The records or undefined if not found
 */
export function getRecords ({ data, storeKey } = {}) {
  const dataStore = getDataStore({ data, storeKey })
  if (!dataStore) {
    return undefined
  }
  return dataStore.get(DATA_RECORDS_KEY)
}

/**
 * Filter the records in the data store for a store key
 * @param {Object} param0 The parameters
 * @param {Object} param0.data The data state from redux
 * @param {String} param0.storeKey The store key
 * @param {Function} predicate The function to check all views, the view
 * will be returned when the function returns true
 * @returns {Map} The records filter using the predicate
 */
export function filterRecords ({ data, storeKey } = {}, predicate) {
  if (!isFunction(predicate)) {
    throw new Error('The predicate param is expected to be a function')
  }
  const records = getRecords({ data, storeKey })
  if (!records) {
    return Map()
  }
  return records.filter(predicate)
}

/**
 * Get all of the records associated with a view
 * @param {Object} param0 The parameters
 * @param {Object} param0.data The data state from redux
 * @param {String} param0.storeKey The store key
 * @param {Map} param0.options The options passed to the view
 * @returns {Map} The view records
 */
export function getViewRecords ({ data, storeKey, options = Map() } = {}) {
  const view = findView({ data, storeKey, options })
  if (!view) {
    return OrderedMap()
  }
  const records = getRecords({ data, storeKey })
  const viewRecordsKeys = view.get(DATA_VIEWS_RECORD_KEYS_KEY)
  return viewRecordsKeys.reduce((acc, recordKey) => {
    const record = records.get(recordKey)
    if (record) {
      return acc.set(recordKey, record)
    }
    return acc
  }, OrderedMap())
}

/**
 * Get all of the records associated with a view as an immutable list
 * @param {Object} param0 The parameters
 * @param {Object} param0.data The data state from redux
 * @param {String} param0.storeKey The store key
 * @param {Map} param0.options The options passed to the view
 * @returns {List} The view records as a list
 */
export function getViewRecordsAsList ({ data, storeKey, options = Map() } = {}) {
  return getViewRecords({ data, storeKey, options }).toList()
}

/**
 * Given the data redux state and store key get the data store
 * @param {Object} param0 The parameters
 * @param {Object} param0.data The data state from redux
 * @param {String} param0.storeKey The store key
 * @returns {DataStoreRecord|undefined} The data store record if found or undefined
 */
export function getDataStore ({ data, storeKey } = {}) {
  if (!Map.isMap(data)) {
    throw new Error('The data param is expected to be a Map')
  }
  if (!storeKey) {
    throw new Error('The storeKey param is required')
  }
  return data.get(storeKey)
}

/**
 * Given a list of records create the view keys
 * @param {Map} records A dictionary of records
 * @returns {import('immutable').Seq.Indexed} Returns a sequence of keys
 */
export function createViewRecordKeys (records) {
  if (!Map.isMap(records)) {
    throw new Error('The records argument is required to be a Map')
  }
  return records.keySeq().toList()
}

/**
 * Create an ordered map of view records given a list of data keyed by the provided key,
 * when placing the records in the store this will become a regular map (in that use case)
 * order does not matter
 * @param {List} data An arbitrary list of data
 * @param {String} [key='id'] The field on the data to use as the records key
 * @returns {OrderedMap} A dictionary of records keyed by key
 */
export function createViewRecords (data, key = 'id') {
  if (!List.isList(data)) {
    throw new Error('The data argument is required to be a List')
  }
  // Speed up record creation by making records initially a mutable map
  return OrderedMap().withMutations(
    (records) => data.reduce(
      (acc, v) => acc.set(v.get(key), v),
      records,
    ),
  )
}

/**
 * Get all of the application in the data store for a store key
 * @param {Object} param0 The parameters
 * @param {Object} param0.data The data state from redux
 * @param {String} param0.storeKey The store key
 * @returns {Map|undefined} The records or undefined if not found
 */
export function getApplication ({ data, storeKey } = {}) {
  const dataStore = getDataStore({ data, storeKey })
  if (!dataStore) {
    return undefined
  }
  return dataStore.get(DATA_APPLICATION_KEY)
}

/**
 * Get the application data in the data store for a store key and application key
 * @param {Object} param0 The parameters
 * @param {Object} param0.data The data state from redux
 * @param {String} param0.storeKey The store key
 * @param {String} param0.applicationKey The application key
 * @returns {Map|undefined} The records or undefined if not found
 */
export function getApplicationDataByKey ({ data, storeKey, applicationKey } = {}) {
  if (isUndefined(applicationKey)) {
    throw new Error('The applicationKey parameter cannot be undefined')
  }
  const dataStore = getDataStore({ data, storeKey })
  if (!dataStore) {
    return undefined
  }
  return dataStore.getIn([DATA_APPLICATION_KEY, applicationKey])
}

/**
 * Filter the application in the data store for a store key
 * @param {Object} param0 The parameters
 * @param {Object} param0.data The data state from redux
 * @param {String} param0.storeKey The store key
 * @param {Function} predicate The function to check all views, the view
 * will be returned when the function returns true
 * @returns {Map} The application items filtered using the predicate
 */
export function filterApplication ({ data, storeKey } = {}, predicate) {
  if (!isFunction(predicate)) {
    throw new Error('The predicate param is expected to be a function')
  }
  const application = getApplication({ data, storeKey })
  if (!application) {
    return Map()
  }
  return application.filter(predicate)
}

export const sortASC = (a, b) => {
  if (a < b) {
    return -1
  }

  if (a > b) {
    return 1
  }

  return 0
}

export const sortDESC = (a, b) => {
  if (a > b) {
    return -1
  }

  if (a < b) {
    return 1
  }

  return 0
}
